import React from 'react';
import { Box } from '@mui/material';
import {
    EXPLORER_FILTER_CONTROL_HEIGHT,
    EXPLORER_FILTER_SELECT_WIDTH,
} from './utils';

/** Named filter in the shared row. The search field grows; the other controls keep a fixed width. */
export type ExplorerFilterSlotName =
    | 'search'
    | 'groupBy'
    | 'channelType'
    | 'category'
    | 'placement'
    | 'dates'
    | 'export';

function slotFlex(slot: ExplorerFilterSlotName, grow: boolean, widthPx: number): string {
    if (grow || slot === 'search') return '1 1 180px';
    if (slot === 'dates' || slot === 'export') return '0 0 auto';
    return `0 0 ${widthPx}px`;
}

/** One filter. Direct child of ExplorerFilterRow, or inside a display:contents wrapper. */
export function ExplorerFilterSlot({
    slot,
    grow = false,
    width,
    children,
}: {
    slot: ExplorerFilterSlotName;
    /** Fill leftover row width. Search already does this. */
    grow?: boolean;
    /** Override the default fixed dropdown width for this slot. */
    width?: number;
    children: React.ReactNode;
}) {
    if (children == null || children === false) return null;
    const expands = grow || slot === 'search';
    const hugsContent = slot === 'dates' || slot === 'export';
    const fixedWidth = expands || hugsContent ? undefined : (width ?? EXPLORER_FILTER_SELECT_WIDTH);
    return (
        <Box
            sx={{
                flex: { xs: '1 1 100%', md: slotFlex(slot, grow, fixedWidth ?? EXPLORER_FILTER_SELECT_WIDTH) },
                width: expands || hugsContent ? 'auto' : fixedWidth,
                maxWidth: { xs: '100%', md: expands || hugsContent ? 'none' : fixedWidth },
                minWidth: { xs: 0, md: expands ? 180 : hugsContent ? 0 : fixedWidth },
                ...(expands || !hugsContent
                    ? {
                          '& > *': {
                              width: '100% !important',
                              maxWidth: '100%',
                              minWidth: '0 !important',
                          },
                          ...(slot !== 'search'
                              ? {
                                    '& .MuiInputBase-root': {
                                        height: EXPLORER_FILTER_CONTROL_HEIGHT,
                                        boxSizing: 'border-box',
                                    },
                                }
                              : {}),
                      }
                    : slot === 'dates'
                      ? {
                            '& .MuiInputBase-root': {
                                height: EXPLORER_FILTER_CONTROL_HEIGHT,
                                boxSizing: 'border-box',
                                width: 'auto',
                            },
                        }
                      : {}),
            }}
        >
            {children}
        </Box>
    );
}

/**
 * Filter row shared by Explorer tabs. Controls sit on one line and the search
 * field takes whatever width the other filters do not use.
 */
const ExplorerFilterRow = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
    ({ children }, ref) => (
        <Box
            ref={ref}
            sx={{
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                gap: 1.5,
                width: '100%',
            }}
        >
            {children}
        </Box>
    ),
);
ExplorerFilterRow.displayName = 'ExplorerFilterRow';

export default ExplorerFilterRow;
